import { useQuery } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import { getAddress } from 'viem'
import { E_LEND_CHAIN_ID, EULER_SWAP_API_URL } from '@/config/euler-contracts'
import {
  EulerSwapApiResponse,
  EulerSwapQuoteParams,
  EulerSwapQuoteParamsExtended,
  EulerSwapResponse,
  SwapperMode,
  UseEulerSwapQuoteResult,
} from './types'
import { sortQuotesByPreference } from './routeValidation'

/** Swap API request timeout in milliseconds. Cannot be lower — backend is slow to respond.
 *  React Query's `retry: 2` already handles transient failures with backoff. */
const SWAP_QUOTE_TIMEOUT_MS = 30_000

function formatSwapApiError(status: number, apiMessage?: string): string {
  return apiMessage ? `Euler swap API error: ${status} - ${apiMessage}` : `Euler swap API error: ${status}`
}

function formatNoQuoteError(apiMessage?: string): string {
  return apiMessage ? `No swap quote available: ${apiMessage}` : 'No swap quote available'
}

export function useEulerSwapQuote({
  tokenIn,
  tokenOut,
  amount,
  vaultIn,
  receiver,
  accountIn,
  accountOut,
  origin,
  slippage = 0.1,
  enabled = true,
  skipSweepDepositOut = false,
}: EulerSwapQuoteParams): UseEulerSwapQuoteResult {
  const { address: userAddress } = useAccount()

  // Resolve optional parameters for consistent query key
  const resolvedOrigin = origin ?? userAddress ?? ''
  const resolvedAccountIn = accountIn ?? userAddress ?? ''
  const resolvedAccountOut = accountOut ?? userAddress ?? ''

  const query = useQuery({
    queryKey: [
      'eulerSwapQuote',
      tokenIn,
      tokenOut,
      amount,
      vaultIn,
      receiver,
      userAddress,
      slippage,
      skipSweepDepositOut,
      resolvedOrigin,
      resolvedAccountIn,
      resolvedAccountOut,
    ],
    queryFn: async (): Promise<EulerSwapResponse[]> => {
      // All address params (tokenIn, tokenOut, vaultIn, receiver, userAddress) are
      // validated here and by the `enabled` guard. The resolved* values fall back to
      // userAddress, so they're covered too. Additional isAddress() checks are
      // unnecessary — getAddress() below will throw on invalid input, and the
      // `enabled` condition prevents queryFn from running with empty values.
      if (!tokenIn || !tokenOut || !amount || !vaultIn || !receiver || !userAddress) {
        return []
      }

      // Safety net for resolved addresses (defensive — see comment above).
      // Falsy check already covers undefined, null, and empty strings.
      // Explicit isAddress() is unnecessary — getAddress() below throws on
      // any non-checksummable input, so invalid formats never reach the API.
      if (!resolvedOrigin || !resolvedAccountIn || !resolvedAccountOut) {
        return []
      }

      // Calculate deadline (30 minutes from now, matches Euler's SWAP_DEFAULT_DEADLINE = 1800)
      const deadline = Math.floor(Date.now() / 1000) + 1800

      // Ensure all addresses are checksummed (API may be case-sensitive)
      const params = new URLSearchParams({
        chainId: E_LEND_CHAIN_ID.toString(),
        tokenIn: getAddress(tokenIn),
        tokenOut: getAddress(tokenOut),
        amount,
        targetDebt: '0',
        currentDebt: '0',
        receiver: getAddress(receiver),
        vaultIn: getAddress(vaultIn),
        origin: getAddress(resolvedOrigin),
        accountIn: getAddress(resolvedAccountIn),
        accountOut: getAddress(resolvedAccountOut),
        slippage: slippage.toString(),
        deadline: deadline.toString(),
        swapperMode: SwapperMode.EXACT_IN.toString(),
        isRepay: 'false',
        dustAccount: getAddress(resolvedOrigin),
      })

      // Skip final deposit when receiver is a wallet (not a vault)
      // This is needed for wallet and pooled market destinations
      if (skipSweepDepositOut) {
        params.set('skipSweepDepositOut', 'true')
      }

      const url = `${EULER_SWAP_API_URL}/swaps?${params.toString()}`

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), SWAP_QUOTE_TIMEOUT_MS)

      try {
        const response = await fetch(url, { signal: controller.signal })

        if (!response.ok) {
          let apiMessage: string | undefined
          try {
            const errorBody = await response.json()
            apiMessage = errorBody?.message
          } catch {}
          throw new Error(formatSwapApiError(response.status, apiMessage))
        }

        const data: EulerSwapApiResponse = await response.json()

        if (!data.success || !data.data || data.data.length === 0) {
          throw new Error(formatNoQuoteError(data.message))
        }

        return sortQuotesByPreference(data.data)
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.error('[Euler swap] Quote request timed out', {
            url,
            timeoutMs: SWAP_QUOTE_TIMEOUT_MS,
            tokenIn,
            tokenOut,
          })
          throw new Error('Swap quote request timed out')
        }
        throw error
      } finally {
        // Timeout is always cleaned up here. React Query handles cancellation
        // on unmount (query is GC'd), so no unbounded timeout risk.
        clearTimeout(timeoutId)
      }
    },
    enabled:
      enabled &&
      !!tokenIn &&
      !!tokenOut &&
      !!amount &&
      !!vaultIn &&
      !!receiver &&
      !!userAddress &&
      BigInt(amount || '0') > 0n,
    // Note: Removed tokenIn !== tokenOut check to allow same-underlying swaps
    // Backend supports 1:1 pass-through swaps for same-underlying assets
    staleTime: 10_000, // 10 seconds
    refetchInterval: 12_000, // 12 seconds
    refetchOnMount: 'always', // Always fetch fresh quotes when dialog opens
    gcTime: 30_000,
    retry: 2,
  })

  return {
    quote: query.data?.[0] ?? null,
    quotes: query.data ?? [],
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Extended hook that supports TARGET_DEBT mode for repay operations
 * This is used for:
 * - Repay with Collateral: Withdraw collateral, swap to debt token, repay
 * - Swap Debt: Borrow new token, swap to old debt token, repay old debt
 */
export function useEulerSwapQuoteExtended({
  tokenIn,
  tokenOut,
  amount,
  vaultIn,
  receiver,
  accountIn,
  accountOut,
  origin,
  slippage = 0.1,
  enabled = true,
  swapperMode = SwapperMode.EXACT_IN,
  targetDebt = '0',
  currentDebt = '0',
  isRepay = false,
  dustAccount,
}: EulerSwapQuoteParamsExtended): UseEulerSwapQuoteResult {
  const { address: userAddress } = useAccount()

  // Resolve optional parameters for consistent query key
  const resolvedOrigin = origin ?? userAddress ?? ''
  const resolvedAccountIn = accountIn ?? userAddress ?? ''
  const resolvedAccountOut = accountOut ?? userAddress ?? ''
  const resolvedDustAccount = dustAccount ?? resolvedOrigin

  const query = useQuery({
    queryKey: [
      'eulerSwapQuoteExtended',
      tokenIn,
      tokenOut,
      amount,
      vaultIn,
      receiver,
      userAddress,
      slippage,
      swapperMode,
      targetDebt,
      currentDebt,
      isRepay,
      resolvedAccountIn,
      resolvedAccountOut,
      resolvedOrigin,
      resolvedDustAccount,
    ],
    queryFn: async (): Promise<EulerSwapResponse[]> => {
      if (!tokenIn || !tokenOut || !vaultIn || !receiver || !userAddress) {
        return []
      }

      // Safety net for empty address strings (would cause getAddress to throw).
      // In practice already guarded by `enabled` (!!userAddress) + fallback to userAddress.
      if (!resolvedOrigin || !resolvedAccountIn || !resolvedAccountOut || !resolvedDustAccount) {
        return []
      }

      // Calculate deadline (30 minutes from now, matches Euler's SWAP_DEFAULT_DEADLINE = 1800)
      const deadline = Math.floor(Date.now() / 1000) + 1800

      // Ensure all addresses are checksummed (API may be case-sensitive)
      const params = new URLSearchParams({
        chainId: E_LEND_CHAIN_ID.toString(),
        tokenIn: getAddress(tokenIn),
        tokenOut: getAddress(tokenOut),
        amount: amount || '0',
        targetDebt,
        currentDebt,
        receiver: getAddress(receiver),
        vaultIn: getAddress(vaultIn),
        origin: getAddress(resolvedOrigin),
        accountIn: getAddress(resolvedAccountIn),
        accountOut: getAddress(resolvedAccountOut),
        slippage: slippage.toString(),
        deadline: deadline.toString(),
        swapperMode: swapperMode.toString(),
        isRepay: isRepay.toString(),
        dustAccount: getAddress(resolvedDustAccount),
      })

      const url = `${EULER_SWAP_API_URL}/swaps?${params.toString()}`

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), SWAP_QUOTE_TIMEOUT_MS)

      try {
        const response = await fetch(url, { signal: controller.signal })

        if (!response.ok) {
          let apiMessage: string | undefined
          try {
            const errorBody = await response.json()
            apiMessage = errorBody?.message
          } catch {}
          throw new Error(formatSwapApiError(response.status, apiMessage))
        }

        const data: EulerSwapApiResponse = await response.json()

        if (!data.success || !data.data || data.data.length === 0) {
          throw new Error(formatNoQuoteError(data.message))
        }

        return sortQuotesByPreference(data.data)
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.error('[Euler swap] Extended quote request timed out', {
            url,
            timeoutMs: SWAP_QUOTE_TIMEOUT_MS,
            tokenIn,
            tokenOut,
            swapperMode,
          })
          throw new Error('Swap quote request timed out')
        }
        throw error
      } finally {
        clearTimeout(timeoutId)
      }
    },
    enabled:
      enabled &&
      !!tokenIn &&
      !!tokenOut &&
      !!vaultIn &&
      !!receiver &&
      !!userAddress &&
      tokenIn !== tokenOut &&
      (swapperMode === SwapperMode.TARGET_DEBT ? BigInt(currentDebt || '0') > 0n : BigInt(amount || '0') > 0n),
    staleTime: 10_000, // 10 seconds
    refetchInterval: 12_000, // 12 seconds
    refetchOnMount: 'always', // Always fetch fresh quotes when dialog opens
    gcTime: 30_000,
    retry: 2,
  })

  return {
    quote: query.data?.[0] ?? null,
    quotes: query.data ?? [],
    isLoading: query.isLoading,
    isRefetching: query.isRefetching,
    error: query.error,
    refetch: query.refetch,
  }
}
